/**
 * API Client for generated hooks
 * Provides the base Axios instance and configuration for API calls
 * Uses apiInterceptor.ts for refresh token handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { setupWebManageInterceptors, setAxiosInstance } from '@/utils/apiInterceptor';
import { getConfig as getRuntimeConfig } from './apiConfig';

// Re-export types
export * from './@types';

// Re-export file upload utilities
export { uploadFile, uploadMultipleFiles, uploadBase64File, type FileUploadResult } from './@fileUpload';

const API_CONFIG = {
    get baseUrl() { return getRuntimeConfig().baseUrl; },
    get timeout() { return getRuntimeConfig().timeout; },
    token: 'token',
    refreshTokenKey: 'refreshToken',
};

function createAxiosInstance(): AxiosInstance {
    const instance = axios.create({
        baseURL: API_CONFIG.baseUrl,
        timeout: API_CONFIG.timeout,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // apiInterceptor.ts의 인터셉터 설정 사용
    const interceptors = setupWebManageInterceptors();

    // Request interceptor 설정
    if (interceptors.request) {
        instance.interceptors.request.use(
            interceptors.request.onFulfilled,
            interceptors.request.onRejected
        );
    }

    // Response interceptor 설정
    if (interceptors.response) {
        instance.interceptors.response.use(
            interceptors.response.onFulfilled,
            interceptors.response.onRejected
        );
    }

    // apiInterceptor.ts에서 axios 인스턴스를 사용할 수 있도록 설정
    setAxiosInstance(instance);

    return instance;
}

class ApiClient {
    private instance: AxiosInstance;

    constructor() {
        this.instance = createAxiosInstance();
    }

    getAxiosInstance(): AxiosInstance {
        return this.instance;
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.instance.get<T>(url, config);
    }

    async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.instance.post<T>(url, data, config);
    }

    async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.instance.put<T>(url, data, config);
    }

    async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.instance.patch<T>(url, data, config);
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.instance.delete<T>(url, config);
    }
}

let apiClientInstance: ApiClient | null = null;

export function getApiClient(): ApiClient {
    if (!apiClientInstance) {
        apiClientInstance = new ApiClient();
    }
    return apiClientInstance;
}

export function getConfig() {
    return API_CONFIG;
}

export { ApiClient, API_CONFIG };
